import React, { useEffect, useRef, useState } from 'react'
import { Form, InputGroup } from 'react-bootstrap'
import { Calendar, DateRange, Range } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarWeek, faChevronLeft, faChevronRight } from '@fortawesome/pro-duotone-svg-icons'
import { DateRangeStyled } from '../../pages/asns/asn/edit-asn-modal'
import GlobalStyles from './styles'
import { formatDate, formatDateRange, parseDate, DATE_FORMATS } from '../../utils/datetime'
import styled from 'styled-components'

const MonthSelector = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 280px;
`

const MonthHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const MonthButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  color: #666;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const MonthTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`

const MonthOption = styled.button<{ isSelected?: boolean }>`
  background: ${(props) => (props.isSelected ? '#0d6efd' : 'white')};
  color: ${(props) => (props.isSelected ? 'white' : '#333')};
  border: 1px solid ${(props) => (props.isSelected ? '#0d6efd' : '#ddd')};
  border-radius: 4px;
  padding: 12px 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.isSelected ? '#0d6efd' : '#f8f9fa')};
    border-color: ${(props) => (props.isSelected ? '#0d6efd' : '#0d6efd')};
  }
`

interface CustomDateInputProps {
  value: Date | Range | null
  onChange: (date: Date | Range | null) => void
  placeholder?: string
  required?: boolean
  label?: string
  className?: string
  mode?: 'single' | 'range'
  minDate?: Date
  maxDate?: Date
  keepOpen?: boolean
}

export const CustomDateInput: React.FC<CustomDateInputProps> = ({
  value,
  onChange,
  placeholder = 'MM-DD-YYYY',
  required = false,
  label,
  className = '',
  mode = 'single',
  minDate,
  maxDate,
  keepOpen = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [showInvalidDateError, setShowInvalidDateError] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const isDirtyRef = useRef(false)
  const mouseDownRef = useRef(false)

  // Detect if we're using MM-YYYY format based on placeholder
  const isMonthYearFormat = placeholder === 'MM-YYYY'
  const dateFormat = isMonthYearFormat ? DATE_FORMATS.MONTH_YEAR : DATE_FORMATS.US

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node) && !keepOpen) {
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [keepOpen])

  useEffect(() => {
    const handleWindowMouseUp = () => {
      mouseDownRef.current = false
    }

    window.addEventListener('mouseup', handleWindowMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp)
    }
  }, [])

  useEffect(() => {
    if (value) {
      let formatedDate
      if (mode === 'single' && value instanceof Date) {
        formatedDate = formatDate(value, dateFormat)
        setInputValue(formatedDate)
      } else if (mode === 'range' && 'startDate' in value) {
        const { startDate, endDate } = value
        if (startDate && endDate) {
          formatedDate = formatDateRange({ startDate, endDate }, dateFormat)
          setInputValue(formatedDate)
        }
      }
      validateInput(formatedDate)
    } else {
      setInputValue('')
    }
  }, [value, mode, dateFormat])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    isDirtyRef.current = true
  }

  function validateInput(val: string | null = null) {
    setShowInvalidDateError(false)
    if (!isDirtyRef.current) return // Don't validate if the input wasn't manually changed
    if (mode === 'single') {
      const date = parseDate(val || '')
      if (!date) {
        setShowInvalidDateError(true)
        setInputValue('')
      }
    } else if (mode === 'range') {
      const [startStr, endStr] = (val || '').split(' to ').map((str) => str.trim())
      const startDate = startStr ? parseDate(startStr) : null
      const endDate = endStr ? parseDate(endStr) : null

      if (!(startDate && endDate && startDate <= endDate)) {
        setShowInvalidDateError(true)
        setInputValue('')
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      validateInput(inputValue)
    }
  }

  const handleFocus = () => {
    if (!mouseDownRef.current) {
      setIsFocused(true)
    }
  }

  const handleMouseDown = () => {
    mouseDownRef.current = true
  }

  const handleClick = () => {
    setIsFocused(true)
  }

  const handleCalendarChange = (item: Date | Range) => {
    onChange(item)
    setIsFocused(false)
    isDirtyRef.current = true
  }

  const handleMonthSelect = (monthIndex: number, year: number) => {
    const selectedDate = new Date(year, monthIndex, 1)
    onChange(selectedDate)
    setIsFocused(false)
    isDirtyRef.current = true
  }

  const handleClear = () => {
    onChange(null)
    setInputValue('')
    isDirtyRef.current = true
  }

  const MonthPicker: React.FC = () => {
    const [currentYear, setCurrentYear] = useState(
      value instanceof Date ? value.getFullYear() : new Date().getFullYear()
    )

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

    const handleMonthClick = (monthIndex: number, e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      handleMonthSelect(monthIndex, currentYear)
    }

    const handleYearChange = (direction: 'prev' | 'next', e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setCurrentYear((prev) => (direction === 'prev' ? prev - 1 : prev + 1))
    }

    const isMonthSelected = (monthIndex: number) => {
      return value instanceof Date && value.getFullYear() === currentYear && value.getMonth() === monthIndex
    }

    return (
      <MonthSelector>
        <MonthHeader>
          <MonthButton
            onClick={(e) => handleYearChange('prev', e)}
            disabled={minDate && currentYear <= minDate.getFullYear()}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </MonthButton>
          <MonthTitle>{currentYear}</MonthTitle>
          <MonthButton
            onClick={(e) => handleYearChange('next', e)}
            disabled={maxDate && currentYear >= maxDate.getFullYear()}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </MonthButton>
        </MonthHeader>
        <MonthGrid>
          {months.map((month, index) => (
            <MonthOption key={month} onClick={(e) => handleMonthClick(index, e)} isSelected={isMonthSelected(index)}>
              {month}
            </MonthOption>
          ))}
        </MonthGrid>
      </MonthSelector>
    )
  }

  return (
    <div ref={containerRef} className={className}>
      {label && <Form.Label>{label}</Form.Label>}
      <InputGroup>
        <Form.Control
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          required={required}
          placeholder={mode === 'range' ? 'MM-DD-YYYY to MM-DD-YYYY' : placeholder}
        />
        {value && (
          <GlobalStyles.ClearInputButton type="button" onClick={handleClear}>
            Clear
          </GlobalStyles.ClearInputButton>
        )}
        <InputGroup.Text>
          <FontAwesomeIcon icon={faCalendarWeek} />
        </InputGroup.Text>
      </InputGroup>
      {showInvalidDateError && isDirtyRef.current && (
        <Form.Text className="text-danger">Invalid date format. Please enter a valid date.</Form.Text>
      )}
      {(isFocused || keepOpen) && (
        <DateRangeStyled>
          {isMonthYearFormat ? (
            <MonthPicker />
          ) : mode === 'single' ? (
            <Calendar
              date={value instanceof Date ? value : new Date()}
              onChange={handleCalendarChange}
              className="hide-in-percy"
              minDate={minDate}
              maxDate={maxDate}
            />
          ) : (
            <DateRange
              ranges={
                value && 'startDate' in value
                  ? [value]
                  : [
                      {
                        startDate: new Date(),
                        endDate: new Date(),
                        key: 'selection',
                        color: '#0d6efd',
                      },
                    ]
              }
              onChange={(ranges) => handleCalendarChange(ranges.selection)}
              rangeColors={['#0d6efd']}
              className="hide-in-percy"
              minDate={minDate}
              maxDate={maxDate}
              months={1}
              direction="horizontal"
              showDateDisplay={false}
              showMonthAndYearPickers={true}
              showMonthArrow={true}
            />
          )}
        </DateRangeStyled>
      )}
    </div>
  )
}
